import { useState, useEffect, useMemo } from 'react'
import { PageHeader, SearchInput, StatusBadge, Modal, EmptyState } from '../components/UI'
import { get, post, put } from '../utils/api'
import { formatCurrency, formatDate, priorityColor } from '../utils/helpers'
import { IconPlus, IconFilter, IconDownload, IconList, IconLayoutGrid, IconUsers, IconMail, IconPhone, IconBuilding } from '@tabler/icons-react'

const STATUS_OPTIONS = ['all','new','contacted','qualified','proposal_sent','negotiation','closed_won','closed_lost']
const EVENT_TYPES = ['all','Conference','Wedding','Corporate Meeting','Social Event']

export default function LeadsPage() {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [view, setView] = useState('table')
  const [showAdd, setShowAdd] = useState(false)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)

  const loadLeads = async () => {
    try {
      const params = {}
      if (search) params.search = search
      if (statusFilter !== 'all') params.status = statusFilter
      if (typeFilter !== 'all') params.event_type = typeFilter
      setLeads(await get('/leads', params))
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  useEffect(() => { loadLeads() }, [statusFilter, typeFilter])

  const filtered = useMemo(() => {
    if (!search) return leads
    const s = search.toLowerCase()
    return leads.filter(l => l.company.toLowerCase().includes(s) || (l.contact_name||'').toLowerCase().includes(s))
  }, [leads, search])

  const stats = { total: leads.length, qualified: leads.filter(l => l.status === 'qualified').length,
    inProgress: leads.filter(l => ['contacted','proposal_sent','negotiation'].includes(l.status)).length,
    newThisWeek: leads.filter(l => { const d = new Date(l.created_at); return (Date.now() - d) < 7*86400000 }).length }

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/leads', formData); setShowAdd(false); setFormData({}); loadLeads() }
    catch (err) { alert(err.message) }
    finally { setSaving(false) }
  }

  const handleUpdate = async () => {
    setSaving(true)
    try { await put(`/leads/${editing.id}`, formData); setShowAdd(false); setEditing(null); setFormData({}); loadLeads() }
    catch (err) { alert(err.message) }
    finally { setSaving(false) }
  }

  const openEdit = (lead) => { setEditing(lead); setFormData({ ...lead }); setShowAdd(true) }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Leads" subtitle={`${leads.length} total leads`}
        actions={<>
          <button className="btn btn-outline-secondary btn-sm"><IconFilter size={16} className="me-1" />Filters</button>
          <button className="btn btn-outline-secondary btn-sm"><IconDownload size={16} className="me-1" />Export</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormData({}); setShowAdd(true) }}><IconPlus size={16} className="me-1" />New Lead</button>
        </>} />

      {/* Quick Stats */}
      <div className="row row-cards mb-3">
        {[{ label: 'Total', value: stats.total, color: 'primary' },
          { label: 'Qualified', value: stats.qualified, color: 'purple' },
          { label: 'In Progress', value: stats.inProgress, color: 'yellow' },
          { label: 'New This Week', value: stats.newThisWeek, color: 'green' }
        ].map(s => (
          <div key={s.label} className="col-6 col-lg-3">
            <div className="card stat-mini"><div className="card-body">
              <div className="subheader">{s.label}</div>
              <div className="h3 mb-0">{s.value}</div>
            </div></div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search leads..." />
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t === 'all' ? 'All Event Types' : t}</option>)}
        </select>
        <div className="ms-auto btn-group">
          <button className={`btn btn-sm ${view==='table'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setView('table')}><IconList size={16} /></button>
          <button className={`btn btn-sm ${view==='card'?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setView('card')}><IconLayoutGrid size={16} /></button>
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState icon={IconUsers} title="No leads found" message="Try adjusting your filters or add a new lead." /> : view === 'table' ? (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover">
              <thead><tr><th>Company</th><th>Contact</th><th>Source</th><th>Event Type</th><th>Event Date</th><th>Rooms</th><th>Value</th><th>Priority</th><th>Status</th></tr></thead>
              <tbody>
                {filtered.map(l => (
                  <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(l)}>
                    <td className="fw-medium">{l.company}</td>
                    <td><div>{l.contact_name}<div className="text-secondary" style={{fontSize:'0.8rem'}}>{l.email}</div></div></td>
                    <td>{l.source}</td>
                    <td>{l.event_type}</td>
                    <td>{l.event_date ? formatDate(l.event_date, { month:'short', day:'numeric' }) : '—'}</td>
                    <td>{l.room_count > 0 ? <span className="badge bg-blue-lt">{l.room_count} rooms</span> : '—'}</td>
                    <td className="fw-medium">{formatCurrency(l.estimated_value)}</td>
                    <td><span className={`badge bg-${priorityColor(l.priority)}-lt`}>{l.priority}</span></td>
                    <td><StatusBadge status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="row row-cards">
          {filtered.map(l => (
            <div key={l.id} className="col-md-6 col-xl-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <div><div className="fw-bold">{l.company}</div><div className="text-secondary" style={{fontSize:'0.8rem'}}>{l.contact_name}</div></div>
                    <span className={`badge bg-${priorityColor(l.priority)}-lt`}>{l.priority}</span>
                  </div>
                  <div className="d-flex flex-column gap-1 mb-3" style={{fontSize:'0.85rem'}}>
                    <span className="d-flex align-items-center gap-1 text-secondary"><IconMail size={14} />{l.email}</span>
                    <span className="d-flex align-items-center gap-1 text-secondary"><IconPhone size={14} />{l.phone}</span>
                    <span className="d-flex align-items-center gap-1 text-secondary"><IconBuilding size={14} />{l.event_type} — {l.event_date ? formatDate(l.event_date,{month:'short',day:'numeric'}):''}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="fw-bold">{formatCurrency(l.estimated_value)}</span>
                    <StatusBadge status={l.status} />
                  </div>
                  {l.room_count > 0 && <span className="badge bg-blue-lt mt-2">{l.room_count} rooms / {l.room_nights} nights</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Lead Modal */}
      <Modal open={showAdd} onClose={() => { setShowAdd(false); setEditing(null) }} title={editing ? 'Edit Lead' : 'New Lead'} wide>
        <div className="row g-3">
          {[{label:'Company Name',key:'company',type:'text',ph:'e.g. Marriott'},{label:'Contact Name',key:'contact_name',type:'text',ph:'Full name'},
            {label:'Email',key:'email',type:'email',ph:'email@company.com'},{label:'Phone',key:'phone',type:'tel',ph:'(404) 555-0000'},
            {label:'Source',key:'source',type:'select',options:['Website','Referral','Cold Call','Past Client','Trade Show','RFP','Walk-In','Other']},
            {label:'Status',key:'status',type:'select',options:['new','contacted','qualified','proposal_sent','negotiation','closed_won','closed_lost']},
            {label:'Event Type',key:'event_type',type:'select',options:['Conference','Wedding','Corporate Meeting','Social Event','Other']},
            {label:'Event Start',key:'event_date',type:'date'},{label:'Event End',key:'event_end_date',type:'date'},
            {label:'Room Count',key:'room_count',type:'number',ph:'0'},{label:'Room Nights',key:'room_nights',type:'number',ph:'0'},
            {label:'Estimated Value',key:'estimated_value',type:'number',ph:'0'},
            {label:'Priority',key:'priority',type:'select',options:['high','medium','low']}
          ].map(f => (
            <div key={f.key} className="col-md-6">
              <label className="form-label">{f.label}</label>
              {f.type === 'select' ? (
                <select className="form-select" value={formData[f.key]||''} onChange={e => setFormData({...formData,[f.key]:e.target.value})}>
                  <option value="">Select...</option>
                  {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                </select>
              ) : <input type={f.type} className="form-control" placeholder={f.ph} value={formData[f.key]||''} onChange={e => setFormData({...formData,[f.key]:e.target.value})} />}
            </div>
          ))}
          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={formData.notes||''} onChange={e => setFormData({...formData,notes:e.target.value})} />
          </div>
          {formData.status === 'closed_lost' && (
            <>
              <div className="col-12"><hr className="my-1" /><h4 className="text-danger mb-0">Lost Business Details</h4></div>
              <div className="col-md-4"><label className="form-label">Reason</label>
                <select className="form-select" value={formData.lost_reason||''} onChange={e => setFormData({...formData,lost_reason:e.target.value})}>
                  <option value="">Select...</option>
                  {['Price','Dates Unavailable','Space Unavailable','Went to Competitor','Budget Cut','Event Cancelled','No Response','Other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <div className="col-md-4"><label className="form-label">Lost To (Competitor)</label><input className="form-control" value={formData.lost_to||''} onChange={e => setFormData({...formData,lost_to:e.target.value})} placeholder="Hotel or venue name" /></div>
              <div className="col-md-4"><label className="form-label">Lost Date</label><input type="date" className="form-control" value={formData.lost_date||''} onChange={e => setFormData({...formData,lost_date:e.target.value})} /></div>
              <div className="col-12"><label className="form-label">Lost Notes</label><textarea className="form-control" rows={2} value={formData.lost_notes||''} onChange={e => setFormData({...formData,lost_notes:e.target.value})} /></div>
            </>
          )}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => { setShowAdd(false); setEditing(null) }}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={editing ? handleUpdate : handleCreate}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Lead'}</button>
        </div>
      </Modal>
    </>
  )
}
